import { Component, OnInit, ViewChild } from '@angular/core';
import { AccountService } from 'src/app/core/services/account.service';
import { lastValueFrom, take } from 'rxjs';
import { Router } from '@angular/router';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { User } from 'src/app/core/models/user';
import { NgxSpinnerService } from 'ngx-spinner';
import swal from 'sweetalert2';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { NgForm } from '@angular/forms';
import { iSignup } from '../assist-sign-up/assist-sign-up.component';

interface Plan {
  id: string;
  plan_id: string;
  plan_name: string;
  tenor: number;
  roi: number;
  emi_amount: number;
  navigate_url: string;
}

interface LenderData {
  status: string;
  continue: boolean;
  lender: string;
  lenderId: string;
  plans: Plan[];
  logo: string;
  loanAmount: number;
  lenderCode: string;
  inprincipalAmount: string;
  penalCharges: string;
  penalChargesDesc?: string;
  creditScore: string;
  isEnabled: boolean;
  selectedTenure: number;
  canEdit?: boolean;
}

@Component({
  selector: 'app-lender-selection',
  templateUrl: './lender-selection.component.html',
  styleUrl: './lender-selection.component.scss'
})
export class LenderSelectionComponent implements OnInit {
  @ViewChild('editForm') editForm!: NgForm;

  showEditPopup: boolean = false;
  bsConfig: Partial<BsDatepickerConfig> = {
    dateInputFormat: 'YYYY-MM-DD',
    showWeekNumbers: false
  };
  minDate: Date;
  maxDate: Date;

  travelDetails: Partial<iSignup> = {
    travelType: '',
    travelDate: '',
    travelCost: null,
    origin: '',
    destination: ''
  };

  constructor(private apiService: AccountService, private router: Router, private spinner: NgxSpinnerService) {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 1);
    this.minDate = tomorrow;
    this.maxDate = nextYear;
  }
  async ngOnInit() {
    const customerToken = MemoryStorageService.getItem('customerToken');
    if (customerToken) {
      this.spinner.show();
      try {
        const plans = await lastValueFrom(this.apiService.GetLenderSelection(customerToken));

        if (plans['data']) {
          this.approvedLenders = plans['data'].filter(l => l.status === 'Approved');
          this.rejectedLenders = plans['data'].filter(l => l.status !== 'Approved');
          console.log(plans);

          if (plans['extra'] && plans['extra'].travelDetails) {
            this.travelDetails = plans['extra'].travelDetails;
          }

          // remove finzy temporarily
          this.approvedLenders = this.approvedLenders.filter(l => l.lender !== 'Finzy');
          this.rejectedLenders = this.rejectedLenders.filter(l => l.lender !== 'Finzy');

          if (this.approvedLenders.length === 0) {
            this.router.navigateByUrl('/assist-journey/sorry');
          }
        } else {
          if (plans['error'] === 'No plan generated') {
            this.router.navigateByUrl('/assist-journey/assist-underwrite');
          }
          else if (plans['error'].includes("Sorry, you are not eligible for a credit line at this time")) {
            this.rejected = true;
            swal.fire({
              text: plans['error'],
              //icon: 'error',
              confirmButtonText: 'OK'
            }).then((result) => {
              if (result.isConfirmed) {
                this.router.navigateByUrl('/assist-journey/sorry');
                return;
              }
            });
          }
          else if (plans['error'] === 'Invalid lender') {
            this.router.navigateByUrl('/assist-journey/session-expired');
          }
        }

      } catch (error: any) {
        if (error.status === 0) {
          swal.fire("No internet connection. Please check your network and try again.");
          return;
        }
        if (error?.status === 401 && error?.error?.message === 'Token has expired') {
          // Redirect to /abc if token expired
          this.router.navigateByUrl('/assist-journey/session-expired');
        } else {
          // Handle other errors (optional)
          console.error('API Error:', error);
        }
      } finally {
        this.spinner.hide();
      }
    } else {
      this.router.navigateByUrl('/assist-journey/session-expired');
    }
  }


  approvedLenders: LenderData[] = [];
  rejectedLenders: LenderData[] = [];

  rejected: boolean = false;

  getTenureRange(plans: Plan[]): string {
    const tenors = plans.map(p => p.tenor);
    const min = Math.min(...tenors);
    const max = Math.max(...tenors);
    return min === max ? `${min} month` : `${min} to ${max} months`;
  }

  getMinEmi(plans: any[]) {
    if (!plans || plans.length === 0) return 0;
    // Filter plans that are eligible and have emi_amount
    const eligibleEmis = plans
      .filter(p => p.plan_eligibility && p.emi_amount)
      .map(p => p.emi_amount);
    return Math.min(...eligibleEmis).toFixed(0);
  }

  getInterestRange(plans: Plan[]): string {
    if (!plans || plans.length === 0) return '-';
    const rois = plans.map(p => p.roi);
    const min = Math.min(...rois);
    const max = Math.max(...rois);
    return min === max ? `${min}%` : `${min}% to ${max}%`;
  }

  getMinProcessingFee(plans: any[]): number {
    return Math.min(...plans.map(p => p.processing_fee));
  }

  getLoanAmountRange(plans: any[]): string {
    const amounts = plans.map(p => p.loan_amount);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    return min === max ? `${min}` : `${min} to ${max}`;
  }

  // Approx Annual Percentage Rate (APR) calculation
  getAprRange(plans: any[]): string {
    const aprs = plans.map(p => {
      const monthlyRate = p.roi / 100; // assuming ROI given monthly
      const annualRate = (Math.pow(1 + monthlyRate, 12) - 1) * 100;
      return annualRate;
    });
    const min = Math.min(...aprs);
    const max = Math.max(...aprs);
    return min === max
      ? `${min.toFixed(2)}%`
      : `${min.toFixed(2)}% to ${max.toFixed(2)}%`;
  }

  CanContinue(lender: LenderData): boolean {
    return lender.plans.some(r => r.tenor === lender.selectedTenure)
  }

  continueApplication(lender: LenderData) {
    var url = lender.plans.find(r => r.tenor === lender.selectedTenure)?.navigate_url;
    if (url) {
      window.location.href = url;
    }
    else {
      this.router.navigateByUrl('/assist-journey/session-expired');
    }
  }

  SelectLender(lender: LenderData) {
    this.spinner.show();
    MemoryStorageService.setItem('selectedLenderId', lender.lenderId);
    MemoryStorageService.setItem('assistTravelPlans', JSON.stringify(lender.plans));
    MemoryStorageService.setItem('loanAmount', JSON.stringify(lender.loanAmount));
    MemoryStorageService.setItem('approvalAmount', lender.inprincipalAmount);
    MemoryStorageService.setItem('creditscore', lender.creditScore);
    const user: User = { token: MemoryStorageService.getItem('customerToken'), userName: '', email: '', mobileNumber: '' }
    this.apiService.setCurrentUser(user);
    this.apiService.currentUserSource.pipe(take(1)).subscribe(() => {
      this.apiService.setMoengageUser(user.token);
    });
    this.spinner.hide();
    this.router.navigateByUrl('/assist-journey/payment-plans/' + lender.lenderCode);
  }

  get showEditButton(): boolean {
    return this.approvedLenders.length > 0 && !!this.approvedLenders[0].canEdit;
  }

  openEditPopup() {
    this.showEditPopup = true;
    // Optionally fetch existing details if needed, or rely on user input
  }

  closeEditPopup() {
    this.showEditPopup = false;
  }

  handleTravelDateChange(value: Date) {
    if (value) {
      const year = value.getFullYear();
      const month = (value.getMonth() + 1).toString().padStart(2, '0');
      const date = value.getDate().toString().padStart(2, '0');
      this.travelDetails.travelDate = `${year}-${month}-${date}`;
    }

  }

  allowOnly(event: any, type: string) {
    const input = event.target as HTMLInputElement;
    let allowedPattern;
    if (type === 'numbers') {
      allowedPattern = /[^0-9]/g;
    } else if (type === 'alphabets') {
      allowedPattern = /[^a-zA-Z\s']/g;
    }
    input.value = input.value.replace(allowedPattern, '');
    if (type === 'numbers') {
      if (input.name === 'travelCost') {
        this.travelDetails.travelCost = input.value ? parseInt(input.value) : null;
      }
    } else {
      if (input.name === 'origin') this.travelDetails.origin = input.value;
      if (input.name === 'destination') this.travelDetails.destination = input.value;
    }
  }

  toUpperCase(event: any) {
    event.target.value = event.target.value.toUpperCase();
    if (event.target.name === 'origin') this.travelDetails.origin = event.target.value;
    if (event.target.name === 'destination') this.travelDetails.destination = event.target.value;
  }

  async updateTravelDetails() {
    if (this.editForm.invalid) {
      return;
    }

    // Basic validations
    if (this.travelDetails.travelCost < 10000 || this.travelDetails.travelCost > 1000000) {
      swal.fire("Travel cost must be between 10,000 and 10,00,000");
      return;
    }

    this.spinner.show();
    try {
      const token = MemoryStorageService.getItem('customerToken');
      // Construct payload - assuming API needs token in header (handled by verifyOTP/login usually) or in body?
      // existing calls use token in header or body.
      // AccountService methods often take model.
      // We need to pass customerToken? Usually it's in header. 
      // AccountService `updateTravelDetails` takes model.

      let formattedDate = this.travelDetails.travelDate;
      if (this.travelDetails.travelDate) {
        const d = new Date(this.travelDetails.travelDate);
        if (!isNaN(d.getTime())) {
          const day = d.getDate().toString().padStart(2, '0');
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const year = d.getFullYear();
          formattedDate = `${year}-${month}-${day}`;
        }
      }

      const payload = {
        travelCost: this.travelDetails.travelCost,
        travelDate: formattedDate,
        travelType: this.travelDetails.travelType,
        origin: this.travelDetails.origin,
        destination: this.travelDetails.destination,
        customerToken: token
      };

      const customerToken = MemoryStorageService.getItem('customerToken');
      const res: any = await lastValueFrom(this.apiService.updateTravelDetails(payload, customerToken));

      if (res && res.data) { // Assuming standard response structure
        swal.fire("Success", "Travel details updated successfully", "success").then(() => {
          this.closeEditPopup();
          // Reload lenders?
          this.ngOnInit();
        });
      } else {
        swal.fire("Error", res?.error?.response_msg || "Failed to update details", "error");
      }
    } catch (error) {
      console.error(error);
      swal.fire("Error", "Something went wrong", "error");
    } finally {
      this.spinner.hide();
    }
  }

}
